import { BonusDaysValidFromEnum } from '@cyberlabrs/helpers';
import { LeaveDurationTypeEnum, LeaveRequestType, PolicyDto } from './leaveRequest.types';

export type OrganizationPublicDto = {
    organizationId: string;
    name: string;
};

export type CreateOrganizationDto = {
    name: string;
    website: string;
    phone: string;
    description?: string;
    address?: AddressDto;
    allowUserToAskToJoin?: boolean;
};

export type PatchOrganizationDto = {
    name?: string;
    website?: string;
    phone?: string;
    address?: AddressDto;
    description?: string;
    contactEmail?: string;
    contactFullname?: string;
    contactPhone?: string;
    dateFormat?: string;
    fax?: string;
    industry?: string | null;
    numOfEmployees?: string | null;
    businessType?: string | null;
    configuration?: ConfigurationDto;
    currency?: CurrencyDto;
    taxNumber?: string;
    registrationNumber?: string;
    taxNumberLabel: string;
    registrationNumberLabel: string;
};

export type OrganizationDto = {
    organizationId: string;
    name: string;
    address?: AddressDto;
    description?: string;
    bankAccountInformation?: BankAccountInformationDto;
    allowUserToAskToJoin?: boolean;
    logoUrl?: string;
};

export type AddressDto = {
    countryId?: string | number;
    state?: string;
    city?: string;
    streetline1?: string;
    streetline2?: string;
};

type BankAccountInformationDto = {};

export type UserAskedToJoin = {
    userSub: string;
    createdAt: Date;
    updatedAt: Date;
};

export type SendInviteEmailsBody = {
    emails: string[];
};

type PolicyWithPendingDto = PolicyDto & {
    pending?: boolean;
};

export type OrganizationWithPolicies = {
    organizationId: string;
    name?: string;
    website?: string;
    phone?: string;
    address?: AddressDto;
    description?: string;
    businessType?: BusinessTypeEnum;
    contactEmail?: string;
    contactFullname?: string;
    contactPhone?: string;
    dateFormat?: string;
    fax?: string;
    industry?: IndustryEnum;
    numOfEmployees?: NumOfEmplyeesEnum;
    policies: PolicyWithPendingDto[];
    nonWorkingTimes: [];
    configuration: ConfigurationDto;
    logoUrl?: string;
    currency?: CurrencyDto;
    updatedAt?: string;
    taxNumber?: string | null;
    registrationNumber?: string | null;
    taxNumberLabel: string;
    registrationNumberLabel: string;
};

type ConfigurationDto = {
    customFields: CustomField[];
};

export type CustomField = {
    label: string;
    name: string;
    value: string;
};

export enum IndustryEnum {
    FINANCE = 'FINANCE',
    OPERATIONS = 'OPERATIONS',
    SALES = 'SALES',
    HUMAN_RESOURCES = 'HUMAN_RESOURCES',
    CUSTOMER_SERVICE = 'CUSTOMER_SERVICE',
    SOFTWARE_DEVELOPMENT = 'SOFTWARE_DEVELOPMENT',
    LEGAL = 'LEGAL',
    IT_SUPPORT = 'IT_SUPPORT',
    MARKETING = 'MARKETING',
    OTHERS = 'OTHERS',
}

export enum NumOfEmplyeesEnum {
    XS = '1-25',
    S = '26-49',
    M = '50-249',
    L = '250-749',
    XL = '750+',
}

export enum BusinessTypeEnum {
    FREELANCE = 'FREELANCE',
    FOR_PROFIT = 'FOR_PROFIT',
    NGO = 'NGO',
}

export interface PublicPolicyDto {
    id: string;
    leaveType: string;
    colour: string;
    description?: string;
    isCarriedOver: boolean;
    maxCarriedOverAmount: number;
    leaveDescriptionRequired: boolean;
    maximumNegativeAmount: number;
}

export type CreatePolicyDto = {
    leaveType: string;
    amount: number;
    description: string;
    isCarriedOver: boolean;
    maxCarriedOverAmount: number;
    carriedOverValidToDay: number;
    carriedOverValidToMonth: number;
    autoApproved: boolean;
    addOnJoin: boolean;
    calculated: boolean;
    colour: string;
    affectedMembers: string[];
    isActive: boolean;
    leaveDescriptionRequired: boolean;
    maximumNegativeAmount: number;
    branchIds: string[];
    shortestLeaveDuration: LeaveDurationTypeEnum;
    loyaltyProgramEnabled: boolean;
    bonusEligibilityYears: number;
    bonusDaysAwarded: number;
    bonusDaysValidFrom: BonusDaysValidFromEnum;
    statusLabel: string;
    statusEmoji: string;
};

export type EditPolicyDto = {
    leaveType?: string;
    amount?: number;
    description?: string;
    isCarriedOver?: boolean;
    maxCarriedOverAmount?: number;
    carriedOverValidToDay?: number;
    carriedOverValidToMonth?: number;
    autoApproved?: boolean;
    addOnJoin?: boolean;
    calculated?: boolean;
    colour?: string;
    isActive?: boolean;
    leaveDescriptionRequired?: boolean;
    maximumNegativeAmount?: number;
    branchIds?: string[];
    shortestLeaveDuration?: LeaveDurationTypeEnum;
    loyaltyProgramEnabled?: boolean;
    bonusEligibilityYears?: number;
    bonusDaysAwarded?: number;
    bonusDaysValidFrom?: BonusDaysValidFromEnum;
    statusLabel?: string;
    statusEmoji?: string;
};

export type TeamDto = {
    teamId: string;
    name: string;
    organizationId: string;
    teamLeadId: string;
    description: string;
    markedAsObsolate: boolean;
    createdAt: Date;
    updatedAt: Date;
};

export type BranchDto = {
    id: string;
    name: string;
    countryId: string;
    organizationId: string;
    weekStartingDay: number;
    isMain: boolean;
    currency?: CurrencyDto;
    workingDays?: WorkingDaysDto;
    timezone?: TimezoneDto;
};

export type BranchPublicDto = {
    id: string;
    name: string;
};

export type CreateBranchDto = {
    name: string;
    countryId: string;
    weekStartingDay: number;
    currencyId?: string;
    workingDays: CreateWorkingDaysDto;
    timezoneId?: string;
};

export type CreateTeamDto = {
    name: string;
    description?: string;
    teamLeadId: string;
};

export type ChangeTeamLeadDto = {
    userSub: string;
};

export type EditBranchDto = {
    name?: string;
    countryId?: string;
    weekStartingDay?: number;
    currencyId?: string;
    workingDays?: CreateWorkingDaysDto;
    timezoneId?: string;
};

export type EditTeamDto = {
    name?: string;
    description?: string;
};

export type BranchesCountDto = {
    count: number;
};

export type HolidayDto = {
    id: string;
    name: string;
    date: Date;
    organizationId: string;
    googleCalendarHolidayId: string;
    googleCalendarCountryCode: string;
    overlappingUsers: [];
};

export type HolidaysWithBranchesDto = {
    id: string;
    name: string;
    date: Date;
    organizationId: string;
    branches: BranchDto[];
};

export type CreateHolidayDto = {
    name: string;
    date: Date;
    branchIds: string[];
};

export type GoogleImportDto = {
    countryId: string;
    year: number;
};

export type GoogleImportHolidayDto = {
    googleCalendarHolidayId: string;
    date: string;
    name: string;
};

export type EditHolidayDto = {
    name?: string;
    date?: Date;
    branchIds?: string[];
};

export type CreateGoogleHolidayDto = {
    name: string;
    date: string;
    googleCalendarHolidayId: string;
    googleCalendarCountryCode: string;
};

export type CreateGoogleHolidaysDto = {
    holidays: CreateGoogleHolidayDto[];
    branchIds: string[];
};

export type CurrencyDto = {
    id: string;
    code: string;
    name: string;
    symbol: string;
};

export type WorkingDaysDto = {
    id: string;
    branchId: string;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
};

export type CreateWorkingDaysDto = {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
};

export type TimezoneDto = {
    id: string;
    code: string;
    timezone: string;
    utcOffset: string;
};

export type PolicyWithBranches = PolicyDto & {
    branchPolicies: BranchDto[];
};

export type FeatureFlagsDto = {
    id: string;
    name: string;
    enabled: boolean;
    config: string;
    createdAt: Date;
    updatedAt: Date;
};

export type SettingsDto = {
    id: string;
    organizationId: string;
    allowTimelogOnDayOff: boolean;
};

export type PolicyAndMedicalLeaveBaseDto = {
    id: string;
    leaveType: string;
    amount: number | null;
    documentRequired: boolean;
    reqDescriptionRequired: boolean;
    branchNames: string[];
};

export type MedicalLeaveRequirements = {
    id: string;
    reqDescriptionRequired: boolean;
    documentRequired: boolean;
    description: string;
};

export type MedicalLeaveRequirementsExtended = MedicalLeaveRequirements & {
    leaveType: LeaveRequestType.MEDICAL_LEAVE;
};

export type EditMedicalLeaveDto = {
    description?: string;
    reqDescriptionRequired?: boolean;
    documentRequired?: boolean;
};

export type EditMedicalLeaveRequestDto = {
    id: string;
    description?: string;
    startDate?: string;
    endDate?: string | null;
    files?: File[];
};

export type MedicalLeaveDto = {
    id: string;
    organizationId: string;
    description: string;
    documentRequired: boolean;
    reqDescriptionRequired: boolean;
    updatedAt: Date;
};

export type PricingPlanPriceDto = {
    id: string;
    stripePriceId: string;
    recurringInterval?: string;
    recurringIntervalCount?: number;
    isDefaultPrice: boolean;
    unitAmount: number;
    currency: {
        id: string;
        code: string;
        symbol: string;
    };
};

export enum SubscriptionStatusEnum {
    TRIAL = 'TRIAL',
    PAID = 'PAID',
    CANCELED = 'CANCELED',
    EXPIRED = 'EXPIRED',
}

type BasePricingPlan = {
    id: string;
    stripeProductId: string;
    name: string;
    description?: string;
    metadata: Record<string, any>;
    features: {
        note?: string;
        text: string;
    }[];
    selected: boolean;
    prices: PricingPlanPriceDto[];
    status: SubscriptionStatusEnum;
    activeUntil: Date | null;
};

export type PricingPlanDto = BasePricingPlan & {
    prices: PricingPlanPriceDto[];
};

export type PricingPlan = BasePricingPlan & {
    price: string;
};

export type CardPaymentMethodDto = {
    id: string;
    brand: string | null;
    displayBrand?: string;
    expMonth?: number | null;
    expYear: number | null;
    funding: string | null;
    last4: string;
    isDefault: boolean;
    billingDetails: {
        name: string | null;
        address: {
            city: string | null;
            country: string | null;
            state: string | null;
            postalCode: string | null;
            line: string | null;
        };
    };
};

export type UpdateCardPaymentMethodDto = {
    address: {
        line1?: string | null;
        city?: string | null;
        state?: string | null;
        postal_code?: string | null;
        country: string;
    };
};

export type ActiveSubscriptionResponseDto = {
    id: string;
    name: string;
    startDate: Date;
    endDate: Date | null;
    status: string;
};
